import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  X,
  Plus,
  Minus,
  Users,
  Tag,
  Dumbbell,
  Save
} from 'lucide-react'
import { useLibraryStore, CustomExercise } from '@/stores/libraryStore'
import { MuscleGroup, Equipment } from '@/types'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import toast from 'react-hot-toast'

const exerciseSchema = z.object({
  name: z.string().min(1, 'Exercise name is required'),
  category: z.enum(['compound', 'isolation', 'cardio', 'plyometric', 'isometric', 'flexibility', 'balance', 'core']),
  muscleGroups: z.array(z.string()).min(1, 'At least one muscle group is required'),
  equipment: z.array(z.string()),
  difficulty: z.enum(['beginner', 'intermediate', 'advanced']).optional(),
  targetSets: z.number().min(1).max(10).optional(),
  targetReps: z.string().optional(),
  restTime: z.number().min(0).max(600).optional(),
  instructions: z.array(z.string()).min(1, 'At least one instruction is required'),
  tags: z.array(z.string()).optional(),
  videoUrl: z.string().url().optional().or(z.literal('')),
  imageUrl: z.string().url().optional().or(z.literal(''))
})

type ExerciseFormData = z.infer<typeof exerciseSchema>

export function EditExerciseModal() {
  const {
    selectedExercise,
    isEditModalOpen,
    setIsEditModalOpen,
    setSelectedExercise,
    updateExercise
  } = useLibraryStore()

  const [saving, setSaving] = useState(false)
  const [instructions, setInstructions] = useState<string[]>([''])
  const [tags, setTags] = useState<string[]>([])
  const [newTag, setNewTag] = useState('')

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors }
  } = useForm<ExerciseFormData>({
    resolver: zodResolver(exerciseSchema),
    defaultValues: {
      category: 'compound',
      muscleGroups: [],
      equipment: [],
      instructions: [''],
      tags: []
    }
  })

  const watchedMuscleGroups = watch('muscleGroups') || []
  const watchedEquipment = watch('equipment') || []

  const muscleGroupOptions: MuscleGroup[] = [
    'chest', 'back', 'shoulders', 'biceps', 'triceps', 'forearms',
    'abs', 'obliques', 'lower-back', 'quads', 'hamstrings', 'glutes', 'calves', 'full-body'
  ]

  const equipmentOptions: Equipment[] = [
    'bodyweight', 'dumbbells', 'barbell', 'kettlebell', 'resistance-bands',
    'pull-up-bar', 'bench', 'cable-machine', 'treadmill', 'bike', 'rowing-machine',
    'yoga-mat', 'foam-roller', 'medicine-ball', 'suspension-trainer'
  ]

  useEffect(() => {
    if (selectedExercise && isEditModalOpen) {
      // Populate form with selected exercise data
      reset({
        name: selectedExercise.name,
        category: selectedExercise.category,
        muscleGroups: selectedExercise.muscleGroups || [],
        equipment: selectedExercise.equipment || [],
        difficulty: selectedExercise.difficulty,
        targetSets: selectedExercise.targetSets,
        targetReps: selectedExercise.targetReps,
        restTime: selectedExercise.restTime,
        instructions: selectedExercise.instructions || [''],
        tags: selectedExercise.tags || [],
        videoUrl: selectedExercise.videoUrl || '',
        imageUrl: selectedExercise.imageUrl || ''
      })

      setInstructions(selectedExercise.instructions || [''])
      setTags(selectedExercise.tags || [])
    }
  }, [selectedExercise, isEditModalOpen, reset])

  const handleClose = () => {
    setIsEditModalOpen(false)
    setSelectedExercise(null)
    reset()
    setInstructions([''])
    setTags([])
    setNewTag('')
  }

  const handleMuscleGroupToggle = (muscleGroup: MuscleGroup) => {
    const current = watchedMuscleGroups
    const updated = current.includes(muscleGroup)
      ? current.filter(mg => mg !== muscleGroup)
      : [...current, muscleGroup]
    setValue('muscleGroups', updated)
  }

  const handleEquipmentToggle = (equipment: Equipment) => {
    const current = watchedEquipment
    const updated = current.includes(equipment)
      ? current.filter(eq => eq !== equipment)
      : [...current, equipment]
    setValue('equipment', updated)
  }

  const handleInstructionChange = (index: number, value: string) => {
    const updated = [...instructions]
    updated[index] = value
    setInstructions(updated)
    setValue('instructions', updated.filter(inst => inst.trim() !== ''))
  }

  const addInstruction = () => {
    setInstructions([...instructions, ''])
  }

  const removeInstruction = (index: number) => {
    if (instructions.length > 1) {
      const updated = instructions.filter((_, i) => i !== index)
      setInstructions(updated)
      setValue('instructions', updated.filter(inst => inst.trim() !== ''))
    }
  }

  const addTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      const updatedTags = [...tags, newTag.trim()]
      setTags(updatedTags)
      setValue('tags', updatedTags)
      setNewTag('')
    }
  }

  const removeTag = (tagToRemove: string) => {
    const updatedTags = tags.filter(tag => tag !== tagToRemove)
    setTags(updatedTags)
    setValue('tags', updatedTags)
  }

  const onSubmit = async (data: ExerciseFormData) => {
    if (!selectedExercise?.id) return

    setSaving(true)
    try {
      const updates: Partial<CustomExercise> = {
        name: data.name,
        category: data.category,
        muscleGroups: data.muscleGroups as MuscleGroup[],
        equipment: data.equipment as Equipment[],
        instructions: data.instructions,
        difficulty: data.difficulty,
        targetSets: data.targetSets,
        targetReps: data.targetReps,
        restTime: data.restTime,
        tags: data.tags,
        videoUrl: data.videoUrl,
        imageUrl: data.imageUrl,
        updatedAt: new Date()
      }

      updateExercise(selectedExercise.id, updates)
      toast.success('Exercise updated successfully!')
      handleClose()
    } catch (error) {
      console.error('Failed to update exercise:', error)
      toast.error('Failed to update exercise')
    } finally {
      setSaving(false)
    }
  }

  if (!isEditModalOpen || !selectedExercise) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Edit Exercise</h2>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Exercise Name *
                </label>
                <input
                  type="text"
                  {...register('name')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Bulgarian Split Squats"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <select
                    {...register('category')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="compound">Compound</option>
                    <option value="isolation">Isolation</option>
                    <option value="cardio">Cardio</option>
                    <option value="plyometric">Plyometric</option>
                    <option value="isometric">Isometric</option>
                    <option value="flexibility">Flexibility</option>
                    <option value="balance">Balance</option>
                    <option value="core">Core</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Difficulty
                  </label>
                  <select
                    {...register('difficulty')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select difficulty</option>
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Muscle Groups */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Muscle Groups *
              </label>
              <div className="grid grid-cols-3 gap-2">
                {muscleGroupOptions.map(muscle => (
                  <label
                    key={muscle}
                    className={`flex items-center p-2 border rounded-lg cursor-pointer transition-colors ${
                      watchedMuscleGroups.includes(muscle)
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={watchedMuscleGroups.includes(muscle)}
                      onChange={() => handleMuscleGroupToggle(muscle)}
                      className="sr-only"
                    />
                    <Users className="w-4 h-4 mr-2" />
                    <span className="text-sm capitalize">{muscle.replace('-', ' ')}</span>
                  </label>
                ))}
              </div>
              {errors.muscleGroups && (
                <p className="mt-1 text-sm text-red-600">{errors.muscleGroups.message}</p>
              )}
            </div>

            {/* Equipment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Required Equipment
              </label>
              <div className="grid grid-cols-3 gap-2">
                {equipmentOptions.map(equipment => (
                  <label
                    key={equipment}
                    className={`flex items-center p-2 border rounded-lg cursor-pointer transition-colors ${
                      watchedEquipment.includes(equipment)
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={watchedEquipment.includes(equipment)}
                      onChange={() => handleEquipmentToggle(equipment)}
                      className="sr-only"
                    />
                    <Dumbbell className="w-4 h-4 mr-2" />
                    <span className="text-sm capitalize">{equipment.replace('-', ' ')}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Target Sets & Reps */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Sets
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  {...register('targetSets', { valueAsNumber: true })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="3"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Reps
                </label>
                <input
                  type="text"
                  {...register('targetReps')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="8-12"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rest Time (seconds)
                </label>
                <input
                  type="number"
                  min="0"
                  max="600"
                  {...register('restTime', { valueAsNumber: true })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="90"
                />
              </div>
            </div>

            {/* Instructions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Instructions *
              </label>
              <div className="space-y-2">
                {instructions.map((instruction, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={instruction}
                      onChange={(e) => handleInstructionChange(index, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder={`Step ${index + 1}`}
                    />
                    {instructions.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeInstruction(index)}
                        className="p-2 text-red-500 hover:text-red-700"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addInstruction}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:text-blue-800"
                >
                  <Plus className="w-4 h-4" />
                  Add Step
                </button>
              </div>
              {errors.instructions && (
                <p className="mt-1 text-sm text-red-600">{errors.instructions.message}</p>
              )}
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Add a tag..."
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {tags.map(tag => (
                      <span
                        key={tag}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm"
                      >
                        <Tag className="w-3 h-3" />
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="ml-1 text-blue-500 hover:text-blue-700"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Media URLs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Video URL
                </label>
                <input
                  type="url"
                  {...register('videoUrl')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="https://youtube.com/..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Image URL
                </label>
                <input
                  type="url"
                  {...register('imageUrl')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="https://example.com/image.jpg"
                />
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit(onSubmit)}
            disabled={saving}
            className="coach-button-primary px-6 py-2 flex items-center disabled:opacity-50"
          >
            {saving ? (
              <>
                <LoadingSpinner size="sm" />
                <span className="ml-2">Updating...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Update Exercise
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}